import {
  Box,
  Grid,
  Icon,
  Input,
  InputGroup,
  InputProps,
  InputRightElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import { ADToBS, BSToAD } from "bikram-sambat-js";
import { addDays, differenceInDays, format, getDay, startOfDay } from "date-fns";
import { useMemo, useEffect, useState, KeyboardEvent } from "react";
import { IconType } from "react-icons";
import { MdChevronLeft, MdChevronRight, MdOutlineExpandMore } from "react-icons/md";
import { RiCalendarTodoFill } from "react-icons/ri";

const BS_MONTHS = [
  "Baisakh",
  "Jestha",
  "Ashadh",
  "Shrawan",
  "Bhadra",
  "Ashwin",
  "Kartik",
  "Mangsir",
  "Poush",
  "Magh",
  "Falgun",
  "Chaitra",
];
const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MIN_BS_YEAR = 1970;
const MAX_BS_YEAR = 2100;

interface BsYearMonth {
  year: number;
  month: number;
}
interface BsDate extends BsYearMonth {
  day: number;
}

function pad(n: number) {
  return String(n).padStart(2, "0");
}

function bsToAdSafe(year: number, month: number, day: number): Date | null {
  try {
    const adStr = BSToAD(`${year}-${pad(month)}-${pad(day)}`);
    const [ay, am, ad] = adStr.split("-").map(Number);
    return new Date(ay, am - 1, ad);
  } catch {
    return null;
  }
}

function adToBs(date: Date): BsDate | null {
  try {
    const bsStr = ADToBS(format(date, "yyyy-MM-dd"));
    const [year, month, day] = bsStr.split("-").map(Number);
    return { year, month, day };
  } catch {
    return null;
  }
}

function nextBsMonth({ year, month }: BsYearMonth): BsYearMonth {
  return month === 12 ? { year: year + 1, month: 1 } : { year, month: month + 1 };
}
function prevBsMonth({ year, month }: BsYearMonth): BsYearMonth {
  return month === 1 ? { year: year - 1, month: 12 } : { year, month: month - 1 };
}

/** Every day of a BS month as its AD equivalent, derived purely from BSToAD point conversions. */
function getBsMonthDays(ym: BsYearMonth): { day: number; adDate: Date }[] | null {
  const firstAd = bsToAdSafe(ym.year, ym.month, 1);
  if (!firstAd) return null;
  const nextFirstAd = bsToAdSafe(nextBsMonth(ym).year, nextBsMonth(ym).month, 1);
  let daysInMonth: number;
  if (nextFirstAd) {
    daysInMonth = differenceInDays(nextFirstAd, firstAd);
  } else {
    daysInMonth = 28;
    while (daysInMonth < 32 && bsToAdSafe(ym.year, ym.month, daysInMonth + 1)) {
      daysInMonth += 1;
    }
  }
  return Array.from({ length: daysInMonth }, (_, i) => ({
    day: i + 1,
    adDate: addDays(firstAd, i),
  }));
}

export interface BsDatePickerProps {
  selected?: Date | null;
  onChange: (date: Date | null) => void;
  size?: InputProps["size"];
  disabled?: boolean;
  placeholder?: string;
  minDate?: Date | null;
  maxDate?: Date | null;
  icon?: IconType;
  /** When true, the input can also be typed into directly ("YYYY-MM-DD" BS), not just picked from the popover calendar. */
  allowTyping?: boolean;
}

/** Validates a typed "YYYY-MM-DD" BS date against the real day count of that BS month before converting. */
function parseTypedBsDate(text: string): Date | null {
  const match = text.trim().match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (year < MIN_BS_YEAR || year > MAX_BS_YEAR) return null;
  if (month < 1 || month > 12) return null;
  const monthDays = getBsMonthDays({ year, month });
  if (!monthDays || day < 1 || day > monthDays.length) return null;
  return bsToAdSafe(year, month, day);
}

export function BsDatePicker({
  selected,
  onChange,
  size = "md",
  disabled,
  placeholder,
  minDate,
  maxDate,
  icon = RiCalendarTodoFill,
  allowTyping = false,
}: BsDatePickerProps) {
  const { isOpen, onOpen, onClose, onToggle } = useDisclosure();
  const [typedValue, setTypedValue] = useState<string | null>(null);
  const [viewMonth, setViewMonth] = useState<BsYearMonth>(
    () => adToBs(selected ?? new Date()) ?? { year: 2082, month: 1 },
  );

  const surfaceBg = useColorModeValue("white", "navy.800");
  const shadow = useColorModeValue(
    "14px 17px 40px 4px rgba(112, 144, 176, 0.18)",
    "14px 17px 40px 4px rgba(112, 144, 176, 0.06)",
  );
  const headerBg = useColorModeValue("background.evident", "whiteAlpha.100");
  const hoverBg = useColorModeValue("surface.100", "whiteAlpha.100");
  const textColor = useColorModeValue("gray.800", "white");

  const selectedTime = selected?.getTime();
  useEffect(() => {
    const ym = adToBs(selected ?? new Date());
    if (ym) setViewMonth(ym);
    setTypedValue(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTime]);

  const monthDays = useMemo(() => getBsMonthDays(viewMonth), [viewMonth.year, viewMonth.month]);
  const leadingBlanks = monthDays?.length ? getDay(monthDays[0].adDate) : 0;

  const prevYm = prevBsMonth(viewMonth);
  const nextYm = nextBsMonth(viewMonth);
  const prevMonthDays = useMemo(() => getBsMonthDays(prevYm), [prevYm.year, prevYm.month]);
  const nextMonthDays = useMemo(() => getBsMonthDays(nextYm), [nextYm.year, nextYm.month]);

  const canGoPrev =
    !minDate || !prevMonthDays?.length || prevMonthDays[prevMonthDays.length - 1].adDate >= startOfDay(minDate);
  const canGoNext =
    !maxDate || !nextMonthDays?.length || nextMonthDays[0].adDate <= startOfDay(maxDate);

  function isDisabledDay(adDate: Date) {
    const day = startOfDay(adDate);
    if (minDate && day < startOfDay(minDate)) return true;
    if (maxDate && day > startOfDay(maxDate)) return true;
    return false;
  }

  const displayValue = useMemo(() => {
    if (!selected) return "";
    const bs = adToBs(selected);
    return bs ? `${bs.year}-${pad(bs.month)}-${pad(bs.day)}` : "";
  }, [selectedTime]);

  const inputValue = typedValue ?? displayValue;

  function commitTypedValue(text: string) {
    if (!text.trim()) {
      setTypedValue(null);
      if (selected) onChange(null);
      return;
    }
    const parsedAd = parseTypedBsDate(text);
    if (parsedAd) onChange(parsedAd);
    setTypedValue(null);
  }

  const yearOptions = useMemo(
    () => Array.from({ length: MAX_BS_YEAR - MIN_BS_YEAR + 1 }, (_, i) => MIN_BS_YEAR + i),
    [],
  );

  return (
    <Popover
      isOpen={disabled ? false : isOpen}
      onOpen={onOpen}
      onClose={onClose}
      placement="bottom-start"
      isLazy
    >
      <PopoverTrigger>
        <InputGroup
          position="relative"
          display="flex"
          alignItems="center"
          justifyContent="center"
          size={size}
        >
          <Input
            size={size}
            value={inputValue}
            placeholder={placeholder}
            isReadOnly={!allowTyping}
            isDisabled={disabled}
            autoComplete="off"
            cursor={disabled ? "default" : allowTyping ? "text" : "pointer"}
            onClick={() => !disabled && !allowTyping && onToggle()}
            onChange={
              allowTyping ? (e) => setTypedValue(e.target.value) : undefined
            }
            onBlur={
              allowTyping ? (e) => commitTypedValue(e.target.value) : undefined
            }
            onKeyDown={
              allowTyping
                ? (e: KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      commitTypedValue(e.currentTarget.value);
                    }
                  }
                : undefined
            }
          />
          <InputRightElement
            color="surface.600"
            cursor={disabled ? "default" : "pointer"}
            onClick={() => !disabled && onToggle()}
          >
            <Icon as={icon} />
          </InputRightElement>
        </InputGroup>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          border="none"
          boxShadow={shadow}
          borderRadius="16px"
          bg={surfaceBg}
          w="272px"
          zIndex="popover"
        >
        <PopoverBody p={3}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
            bg={headerBg}
            borderRadius="10px"
            p={1}
          >
            <Box
              as="button"
              type="button"
              display="flex"
              alignItems="center"
              justifyContent="center"
              boxSize="28px"
              borderRadius="8px"
              opacity={canGoPrev ? 1 : 0.3}
              cursor={canGoPrev ? "pointer" : "not-allowed"}
              transition="background-color 0.15s ease"
              _hover={canGoPrev ? { bg: hoverBg } : {}}
              onClick={() => canGoPrev && setViewMonth(prevYm)}
              aria-label="Previous month"
            >
              <Icon as={MdChevronLeft} boxSize="18px" />
            </Box>

            <Box display="flex" gap={1}>
              <Menu isLazy>
                <MenuButton
                  fontSize="sm"
                  fontWeight="700"
                  px={2}
                  py={1}
                  borderRadius="6px"
                  display="flex"
                  alignItems="center"
                  gap={1}
                  _hover={{ bg: hoverBg }}
                >
                  {BS_MONTHS[viewMonth.month - 1]}
                  <Icon as={MdOutlineExpandMore} boxSize="14px" />
                </MenuButton>
                <MenuList maxH="220px" overflowY="auto" minW="140px" zIndex={1600}>
                  {BS_MONTHS.map((name, idx) => (
                    <MenuItem
                      key={name}
                      fontSize="sm"
                      fontWeight={viewMonth.month === idx + 1 ? "700" : "400"}
                      color={viewMonth.month === idx + 1 ? "primary.500" : "inherit"}
                      onClick={() => setViewMonth({ ...viewMonth, month: idx + 1 })}
                    >
                      {name}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
              <Menu isLazy>
                <MenuButton
                  fontSize="sm"
                  fontWeight="700"
                  px={2}
                  py={1}
                  borderRadius="6px"
                  display="flex"
                  alignItems="center"
                  gap={1}
                  _hover={{ bg: hoverBg }}
                >
                  {viewMonth.year}
                  <Icon as={MdOutlineExpandMore} boxSize="14px" />
                </MenuButton>
                <MenuList maxH="220px" overflowY="auto" minW="100px" zIndex={1600}>
                  {yearOptions.map((year) => (
                    <MenuItem
                      key={year}
                      fontSize="sm"
                      fontWeight={viewMonth.year === year ? "700" : "400"}
                      color={viewMonth.year === year ? "primary.500" : "inherit"}
                      onClick={() => setViewMonth({ ...viewMonth, year })}
                    >
                      {year}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            </Box>

            <Box
              as="button"
              type="button"
              display="flex"
              alignItems="center"
              justifyContent="center"
              boxSize="28px"
              borderRadius="8px"
              opacity={canGoNext ? 1 : 0.3}
              cursor={canGoNext ? "pointer" : "not-allowed"}
              transition="background-color 0.15s ease"
              _hover={canGoNext ? { bg: hoverBg } : {}}
              onClick={() => canGoNext && setViewMonth(nextYm)}
              aria-label="Next month"
            >
              <Icon as={MdChevronRight} boxSize="18px" />
            </Box>
          </Box>

          <Grid templateColumns="repeat(7, 1fr)" gap={1} mb={1}>
            {WEEKDAYS.map((wd) => (
              <Text
                key={wd}
                fontSize="xs"
                textAlign="center"
                color="surface.300"
                fontWeight="600"
              >
                {wd}
              </Text>
            ))}
          </Grid>

          <Grid templateColumns="repeat(7, 1fr)" gap={1}>
            {Array.from({ length: leadingBlanks }, (_, i) => (
              <Box key={`blank-${i}`} />
            ))}
            {(monthDays ?? []).map(({ day, adDate }) => {
              const isSelected = selected
                ? startOfDay(adDate).getTime() === startOfDay(selected).getTime()
                : false;
              const isDisabled = isDisabledDay(adDate);
              const isToday = startOfDay(adDate).getTime() === startOfDay(new Date()).getTime();
              return (
                <Box key={day} display="flex" justifyContent="center" alignItems="center" py="2px">
                  <Box
                    as="button"
                    type="button"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    boxSize="32px"
                    fontSize="sm"
                    borderRadius="full"
                    bg={isSelected ? "primary.500" : "transparent"}
                    color={isSelected ? "white" : isDisabled ? "surface.200" : textColor}
                    fontWeight={isSelected ? "700" : isToday ? "700" : "400"}
                    position="relative"
                    cursor={isDisabled ? "not-allowed" : "pointer"}
                    transition="background-color 0.15s ease, color 0.15s ease"
                    _hover={isDisabled ? {} : { bg: isSelected ? "primary.500" : hoverBg }}
                    disabled={isDisabled}
                    onClick={() => {
                      if (isDisabled) return;
                      onChange(adDate);
                      onClose();
                    }}
                  >
                    {day}
                    {isToday && !isSelected && (
                      <Box
                        position="absolute"
                        bottom="4px"
                        boxSize="3px"
                        borderRadius="full"
                        bg="primary.500"
                      />
                    )}
                  </Box>
                </Box>
              );
            })}
          </Grid>
        </PopoverBody>
      </PopoverContent>
      </Portal>
    </Popover>
  );
}
